import { useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

/* ⛔ (Douglas 09/09/2026) O CANCELADO CONTINUA AVISANDO — SEM TRAVAR O CRM.
   "o cancelado nunca foi problema, ter o cancelado avisando é importante, pois o
   consultor e supervisor têm que ver os seus pedidos cancelados na tela de
   aviso... mas isso não pode travar todo o CRM."
   Antes cada item cancelado abria SEU próprio modal, em cadeia, com beep: 113
   popups por dia — a Brenda levava 18,4, o William 16,7. Agora é UMA tela com o
   total, e cada pedido é confirmado individualmente lá dentro.
   Mesma régua da transferência (Douglas 26/07): NÃO existe "confirmar tudo" —
   seria o jeito mais rápido de zerar a tela sem olhar um pedido sequer. */

export interface AvisoCancelado {
  id: string;
  metadados?: Record<string, unknown> | null;
}

interface Props {
  avisos: AvisoCancelado[];
  onConfirmar: (avisoId: string) => Promise<void>;
}

const texto = (valor: unknown) => (valor == null ? '' : String(valor));

const prazoCurto = (valor: unknown) => {
  const bruto = texto(valor);
  if (!bruto) return '';
  const d = new Date(bruto);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export function AvisosCanceladoAgrupado({ avisos, onConfirmar }: Props) {
  const [aberto, setAberto] = useState(true);
  const [ocupado, setOcupado] = useState<string | null>(null);

  const itens = useMemo(
    () =>
      avisos.map((aviso) => {
        const meta = (aviso.metadados || {}) as Record<string, unknown>;
        return {
          id: aviso.id,
          cliente: texto(meta.nome_cliente) || 'Cliente',
          pedido: texto(meta.numero_pedido),
          produto: texto(meta.produto_nome),
          motivo: texto(meta.motivo_cancelamento),
          prazo: prazoCurto(meta.prazo_recuperacao),
        };
      }),
    [avisos],
  );

  if (!itens.length) return null;

  async function darCiencia(id: string) {
    if (ocupado) return;
    setOcupado(id);
    try {
      await onConfirmar(id);
    } catch {
      alert('Não consegui registrar a ciência. Tente de novo.');
    } finally {
      setOcupado(null);
    }
  }

  if (!aberto) {
    return (
      <button
        type="button"
        onClick={() => setAberto(true)}
        className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-full bg-[linear-gradient(90deg,#4A1320,#5B0A0D)] px-4 py-3 text-sm font-semibold text-white shadow-xl dark:bg-[linear-gradient(90deg,#9D2530,#6C2236)]"
      >
        <AlertTriangle className="h-4 w-4" />
        Pedidos cancelados
        <span className="rounded-full bg-white/20 px-1.5 tabular-nums">{itens.length}</span>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/55 p-4">
      <section className="w-full max-w-[560px] overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
        <header className="flex h-12 items-center gap-2 border-b border-border bg-[#FAF8F4] px-4 dark:bg-[#241A1A]">
          <AlertTriangle className="h-4 w-4 text-[#5B0A0D] dark:text-[#C87484]" />
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-[10px] font-bold uppercase tracking-[1.2px] text-foreground">
              Pedidos cancelados
            </h2>
            <p className="truncate text-[10.5px] text-[#6B5A4D] dark:text-[#B5A8A8]">
              Cada linha é um item que caiu — confira um a um
            </p>
          </div>
          <span className="text-xs font-bold tabular-nums text-[#5B0A0D] dark:text-[#C87484]">{itens.length}</span>
          <button
            type="button"
            onClick={() => setAberto(false)}
            className="rounded px-2 py-1 text-[11px] text-[#6B5A4D] hover:bg-muted dark:text-[#B5A8A8]"
          >
            Depois
          </button>
        </header>

        <div className="max-h-[60vh] overflow-y-auto">
          {itens.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-3 border-b border-border px-4 py-3 last:border-b-0"
            >
              <span aria-hidden className="h-9 w-[3px] shrink-0 rounded-full bg-[#5B0A0D] dark:bg-[#9D2530]" />
              <div className="min-w-0 flex-1">
                <span className="block truncate text-[13.5px] font-semibold text-foreground" title={item.cliente}>
                  {item.cliente}
                </span>
                <span className="block truncate text-[11.5px] text-foreground/80">
                  {item.pedido ? <span className="tabular-nums">Pedido {item.pedido}</span> : 'Pedido —'}
                  {item.produto ? ` · ${item.produto}` : ''}
                </span>
                <span className="block truncate text-[10px] text-[#6B5A4D] dark:text-[#B5A8A8]">
                  {item.motivo || 'Motivo não informado'}
                  {item.prazo ? ` · recuperar até ${item.prazo}` : ''}
                </span>
              </div>
              <button
                type="button"
                disabled={ocupado === item.id}
                onClick={() => darCiencia(item.id)}
                className="shrink-0 whitespace-nowrap rounded-md bg-[linear-gradient(90deg,#4A1320,#5B0A0D)] px-3 py-1.5 text-[11px] font-bold text-white disabled:opacity-60 dark:bg-[linear-gradient(90deg,#9D2530,#6C2236)]"
              >
                {ocupado === item.id ? 'Registrando…' : 'Ciente'}
              </button>
            </div>
          ))}
        </div>

        <footer className="flex h-10 items-center border-t border-border px-4">
          <span className="text-[10.5px] text-[#6B5A4D] dark:text-[#B5A8A8]">
            "Ciente" tira o pedido daqui. "Depois" recolhe a tela — os pedidos continuam esperando.
          </span>
        </footer>
      </section>
    </div>
  );
}
